import { Helmet } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { createContext, useContext } from "react"
import _ from 'lodash'
import { ChevronRightIcon } from "@heroicons/react/20/solid"
import clsx from "clsx"

import { Outlet, useParams, Link, useNavigate } from "../navigation"
import { useDesigns } from "./DesignsProvider"
import { DesignRegistration, DesignFamily as DesignFamilyType } from "./designs"
import { useRoles } from "../user/Auth"
import { IconEdit } from "../components/icons"
import DeleteButton from "../components/DeleteButton"
import { emptyStringAsUndefined } from "../utils/strings"
import DesignComments from "./DesignComments"
import { DesignFamilyTasks } from "./DesignTasks"
import { LinkedTabCard } from "../components/TabCard"
import { FocusedBrowser } from "../documents/FocusedBrowser"
import { design_family } from "../data"
import { CostProvider } from "../costs/CostsProvider"
import { designRegistrationUrl, designFamilyUrl } from "./utils"
import { useProducts } from "../products/ProductsProvider"
import ProtectionIcon from "../components/ProtectionIcon"
import Image from "../components/Image"
import { FamilyBreadCrumb } from "../patents/Family"
import { DesignFamilyCosts as DesignFamilyCostsTable } from "../costs/CostsTable"
import { Commodity } from "src/products/products"

export default function DesignFamilyView() {
    const { t } = useTranslation()
    const { hasDocuments, hasCosts } = useRoles()

    const { reference } = useParams()

    const { familyByReference } = useDesigns()

    const family = familyByReference[reference]

    if (!family) return null

    return <>
        <Helmet>
            <title>{reference} | {t('designs')} | Patent Cockpit</title>
        </Helmet>
        <div className="portfolio-menu">
            <h2 className="flex flex-row gap-1 md:gap-4 whitespace-nowrap shrink min-w-0 grow pr-4">
                <DesignBreadcrumbs {...{ family }} />
            </h2>
        </div>
        <div className="main-content bg-pcx-100 pt-0">
            <div className="w-fit lg:min-w-4xl">
                <DesignFamilyProvider family={family}>
                    <LinkedTabCard links={[
                        { to: ".", label: t("overview") },
                        hasDocuments && { to: "documents", label: t("documents") },
                        hasCosts && { to: "costs", label: t("costs") },
                    ]} />
                </DesignFamilyProvider>
            </div>
        </div>
    </>
}

const DesignFamilyContext = createContext<DesignFamilyType | undefined>(undefined)

function DesignFamilyProvider({ family, children }: { family: DesignFamilyType, children: React.ReactNode }) {
    return <DesignFamilyContext.Provider value={family}>{children}</DesignFamilyContext.Provider>
}

export function useDesignFamily() {
    return useContext(DesignFamilyContext)
}

function ModernChevronRight({className}: {className?: string}) {
    return <ChevronRightIcon className={clsx("h-5 w-5 flex-shrink-0 text-gray-600 self-center", className)} aria-hidden="true" />
}

function DesignBreadcrumbs({ family }: { family: DesignFamilyType }) {
    const { t } = useTranslation()

    return (
        <>
            <Link to="/designs/portfolio" className="modern-h2">{t('portfolio')}</Link>
            <ModernChevronRight />
            <FamilyBreadCrumb {...{
                to: designFamilyUrl(family),
                reference: family.reference,
                familyName: family.description
            }} />
        </>
    )
}

export function DesignFamilyOverview() {
    const { isEditUser } = useRoles()
    const family = useDesignFamily()

    if (!family) return null

    return <div className="grid grid-cols-1 lg:grid-cols-[minmax(40rem,48rem)_400px] xl:grid-cols-[minmax(40rem,48rem)_480px] divide-pcx-200 max-lg:divide-y-2 lg:divide-x-2 gap-2 w-fit">
        <div>
            <DesignFamilyDetails {...{ family, isEditUser }} />
        </div>
        <div className="space-y-5 max-lg:pt-2 lg:pl-2">
            <DesignFamilyTasks familyId={family.designFamilyId} />
            <DesignComments familyId={family.designFamilyId} />
        </div>
        <Outlet />
    </div>
}

function DesignFamilyDetails({ family, isEditUser }: { family: DesignFamilyType, isEditUser: boolean }) {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { registrationsByFamilyId, deleteFamily } = useDesigns()
    const registrations = registrationsByFamilyId[family.designFamilyId] ?? []

    const deleteQuestion = registrations.length > 0
        ? t('delete-design-family-with-registrations', { name: family.reference, count: registrations.length })
        : t('delete-design-family-name', { name: family.reference })

    return <>
        <div className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-4 border-b-2 border-pcx-200 pb-2">
            <div className="h-36">
                <Image {...{
                    entity: design_family,
                    entityId: family.designFamilyId,
                    isEditable: isEditUser,
                    title: family.reference,
                    text: family.description
                }} />
            </div>
            <div>
                <div className="flex flex-row gap-1">
                    <h2 className="grow">{family.reference}</h2>
                    {isEditUser && <>
                        <Link className="btn-primary p-px w-5 h-5" to="edit"><IconEdit /></Link>
                        <DeleteButton className="btn-warn p-px w-5 h-5" deleteAction={() => deleteFamily(family).then(() => navigate("/designs/portfolio"))} question={deleteQuestion} />
                    </>}
                </div>
                {emptyStringAsUndefined(family.externalReference?.trim()) && <div className="label mt-1">{family.externalReference}</div>}
                <p className="mt-2 max-w-prose">{family.description}</p>
            </div>
        </div>
        <div className="pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-x-4 gap-y-2">
                <div className="sm:col-start-2 flex flex-row flex-wrap gap-2 justify-between">
                    <h3 className="">{t('countries')}</h3>
                    {isEditUser && <Link to="add-registration" className="btn-secondary text-sm">{t('add-registration')}</Link>}
                </div>
                <RegistrationsByStatus status="in-preparation" registrations={registrations.filter(r => r.status === 'in-preparation')} hideEmpty />
                <RegistrationsByStatus status="pending" registrations={registrations.filter(r => r.status === 'pending')} />
                <RegistrationsByStatus status="registered" registrations={registrations.filter(r => r.status === 'registered')} />
                <RegistrationsByStatus status="stopped" registrations={registrations.filter(r => r.status === 'stopped')} />

                {family.priorityDate && <>
                    <div className="label sm:text-right">{t('priorityDate')}:</div>
                    <div className="max-sm:pl-2">{family.priorityDate}</div>
                </>}
            </div>
        </div>
        <div className="pt-2 mt-4 border-t-2 border-pcx-200 sm:pl-44">
            <DesignFamilyProducts {...{ familyId: family.designFamilyId }} />
        </div>
    </>
}

function RegistrationsByStatus({ status, registrations = [], hideEmpty = false }: { status: DesignRegistration['status'], registrations: DesignRegistration[], hideEmpty?: boolean }) {
    const { t } = useTranslation()

    if (hideEmpty && registrations.length === 0) return null

    const counts = _.countBy(registrations, 'countryCode')

    return <>
        <div className="label sm:text-right">{t(status)}:</div>
        <div className="max-sm:pl-2 inline-flex flex-row flex-wrap gap-1 self-start items-start ">
            <span className="text-slate-500 hidden last:inline">-</span>
            {_(registrations)
                .sortBy(r => r.countryCode)
                .map(r =>
                    <Link key={r.reference} className="ribbon ribbon-clickable py-px" to={designRegistrationUrl(r)} title={r.reference}>
                        {counts[r.countryCode] > 1
                            ? `${r.countryCode} (${r.reference})`
                            : r.countryCode
                        }
                    </Link>)
                .value()}
        </div>
    </>
}

export function DesignFamilyDocuments() {
    const family = useDesignFamily()

    if (!family) return null

    return <FocusedBrowser entity={design_family} internalReference={family.reference} parentName={family.reference} />
}

export function DesignFamilyCosts() {
    const family = useDesignFamily()

    if (!family?.designFamilyId) return null

    return <CostProvider>
        <DesignFamilyCostsTable designFamilyId={family.designFamilyId} />
        <Outlet />
    </CostProvider>
}

function DesignFamilyProducts({ familyId }: { familyId: number }) {
    const { t } = useTranslation()
    const { isEditUser } = useRoles()

    const { productMappingsByFamilyId } = useDesigns()
    const { commodityById } = useProducts()

    function commodityName({ commodityClass, commodityReference }: Pick<Commodity, 'commodityClass' | 'commodityReference'>) {
        return `${commodityClass ?? t('unclassified')}: ${commodityReference}`
    }

    const protectedProducts = _(productMappingsByFamilyId[familyId] ?? [])
        .filter(m => m.protected)
        .map(m => commodityById[m.commodityId])
        .filter(c => c !== undefined)
        .sortBy((c: Commodity) => commodityName(c).toLocaleLowerCase())
        .value()

    return <>
        {isEditUser && <Link to={`mapping/edit?familyId=${familyId}`} className="btn-secondary p-px h-5 w-5 float-right mr-1"><IconEdit /></Link>}
        <h3>{t('products')}</h3>
        <ul className="list-none text-slate-700 pl-0">
            <li className="hidden last:block text-slate-500">{t('no-affected-products')}</li>
            {protectedProducts.map(({ isThirdParty, commodityId, commodityClass, commodityReference }) =>
                <li key={commodityId} className="flex flex-row gap-3 items-center">
                    <ProtectionIcon {...{ isThirdParty }} /> {commodityName({ commodityClass, commodityReference })}
                </li>)}
        </ul>
    </>
}
